/*
 * Copyright (c) 2025.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * studiebank.component.ts
 * 10-10-2024 - Jelmer Jellema - Spin in het Web B.V. Apeldoorn NL
 * Component StudiebankComponent
 * Selector: studiebank
 */
import {Component, OnInit} from "@angular/core";
import {WPAIBase} from "./wpaibase";
import {ApiService} from "../services/api.service";
import {Studierichting} from "../models/studierichting";
import {discs, profielkeuzes, niveaus} from "./basisdata";
import * as Excel from 'exceljs';
import {hash} from "../types";
import {Profielkeuze} from "../models/profielkeuze";
import {FormsModule} from "@angular/forms";
import {isPlainObject, sortBy, uniq, uniqBy} from "lodash-es";

@Component({
  standalone: true,
  selector: 'studiebank',
  templateUrl: './studiebank.component.html',
  styleUrls: ['studietable.scss', 'studiebank.component.scss'],
  imports: [
    FormsModule
  ]
})
export class StudiebankComponent extends WPAIBase implements OnInit {
  studies: Studierichting[] = [];
  studiesGefilterd: Studierichting[] = [];
  selectedRijNr: number = null;
  filter: {
    niveau?: string,
    disc?: string,
    profielkeuze?: string,
    sector?: string
  } = {};
  protected niveaus: string[] = [];
  protected readonly discs = discs;
  protected readonly profielkeuzes = profielkeuzes;
  protected sectoren: string[] = [];

  constructor(private api: ApiService) {
    super(true);
  }

  async ngOnInit() {
    await this.haalData();
  }

  /**
   * Haal de data opnieuw
   */
  async haalData() {
    this.reset();
    this.studies = sortBy(Studierichting.createAll((await this.api.arrayFromAction('studierichtingen')) || []), ['niveau', 'sector', 'studierichting']);
    this.niveaus = niveaus; // sortBy(uniq(this.studies.map(studie => studie.niveau)));
    this.sectoren = sortBy(uniq(this.studies.map(studie => studie.sector)));
    this.doeFilter(); //filter opnieuw
  }

  /**
   * Controleer of een rij in het filter zit
   * @param studie
   */
  inFilter(studie: Studierichting): boolean {
    if (this.filter.niveau?.length && studie.niveau !== this.filter.niveau) {
      return false;
    }
    if (this.filter.sector?.length && studie.sector !== this.filter.sector) {
      return false;
    }
    if (this.filter.profielkeuze?.length && !this.magProfielkeuze(studie, this.filter.profielkeuze)) {
      return false;
    }
    return !(this.filter.disc?.length && !studie[`d_${this.filter.disc}`]);
  }

  async startImport(e: Event) {
    const ctrl = <HTMLInputElement>e.target;
    const excelfile = ctrl.files[0] || null;
    if (!excelfile) {
      return;
    }

    //we resetten de control even, voor als men vaker wil proberen
    ctrl.value = '';

    //we gaan nu de excelfile doornemen. Pas daarna vragen we bevestiging
    try {
      const workbook = new Excel.Workbook();
      await workbook.xlsx.load(await excelfile.arrayBuffer());
      const sheet = workbook.worksheets[0];
      const header = (<string[]>sheet.getRow(1).values).map(v => v.toLowerCase());
      const verplichteVelden = ['niveau', 'sector', 'studierichting', ...discs]; //deze hebben we nodig
      const alleVelden = [...verplichteVelden, 'opleidingsniveau', ...profielkeuzes]; //en dit zijn alle velden die we zoeken: profielkeuzes zijn niet verplicht in elke excel (bij wo hoef je dus de havo-keuzes niet te noemen). Opleidingsniveau is niet verplicht, en wordt achter de studierichting geplakt als hij er is
      // ook we doen het case onafhankelijk
      const alleVeldenLc = alleVelden.map(v => v.toLowerCase());
      //we verwachten heel wat velden
      for (let key of verplichteVelden) {
        if (!header.includes(key.toLowerCase())) {
          this.api.toast(`Er ontbreken velden: ${key}`, "error");
          return;
        }
      }
      //indexeren maar
      const kolomOpVeld: hash<number> = {};
      for (let [kolom, veld] of header.entries()) {
        veld = veld?.trim();
        if (!veld?.length) {
          continue;
        }
        const veldzonderspaties = veld.replace(/\s+/g, '');
        if (alleVeldenLc.includes(veld)) {
          kolomOpVeld[veld] = kolom;
        } else if (alleVeldenLc.includes(veldzonderspaties)) {
          //misschien spaties teveel?
          kolomOpVeld[veldzonderspaties] = kolom; //bij het echte importeren vinden we dus de juiste kolom, ondanks de foute header
        }
      }
      const nieuweLijst: Studierichting[] = [];
      let errorstop: boolean = false;
      //nu hebben we alle kolommen, runnen maar
      sheet.eachRow((rij, rijnummer) => {
        if (errorstop) {
          return; //skip
        }
        this.log(`Rij`, rijnummer);
        if (rijnummer === 1) {
          return; //de headers, continue
        }
        //we maakten eerst een array kolommen op basis vaij rij.values. Dat werkt niet goed met values met rijke tekst (bijv vreemd font)
        //daarom zetten we het nu even met echCell om. Maar kolom 0 laten we leeg, net zoals bij rij.values;
        // const kolommen = <(string|Record<string, any>)>[]>rij.values; //toch allemaal strings of rich text
        const kolommen: string[] = [""];
        rij.eachCell({includeEmpty: true}, cell => {
          kolommen.push(cell.text);
        })

        if (!kolommen[1]?.length) {
          //lege rij ofzo?
          this.log(`Lege rij`, rij, kolommen);
          return; //continue
        }
        if (kolommen[1] === 'Totaal') {
          this.log(`Totaalrij`);
          //skip de totaalrij
          return; //continue
        }
        const studierichting = new Studierichting();
        studierichting.Profielkeuze = [];
        for (let veld of alleVelden) {
          const kolomnr = kolomOpVeld[veld.toLowerCase()];
          if (!kolomnr) {
            //kolom 0 of undef
            continue;
          }
          let val = kolommen[kolomnr];
          //richtext?
          if (isPlainObject(val)) {
            this.log(`VAL IS OBJECT`);
            this.log(veld, val);
            errorstop = true;
            return;
            /*            if (val.richText && Array.isArray(val.richText)) {
                          val = val.richText.map(rt => rt.text).join('');
                        }
                        else {
                          this.logwarn(`Onbekende waarde`, rijnummer, kolomnr, val);
                          this.api.toast(`Onbekende waarde in kolom ${kolomnr} in rij ${rijnummer}`, "error");
                          errorstop = true;
                          return;
                        }*/
          }

          if (profielkeuzes.includes(veld)) {
            //dit is een profielkeuze, als het niet leeg of nee is, is het interessant
            if (val?.length && val.toLowerCase() !== 'nee') {
              const profielkeuze = new Profielkeuze();
              profielkeuze.profielkeuze = veld;
              if (val?.length && val.toLowerCase() !== 'ja') {
                //dan is het een opmerking
                profielkeuze.opmerking = val;
              }
              studierichting.Profielkeuze.push(profielkeuze); //en erbij bewaren
            }
          } else if (discs.includes(veld)) {
            //een janee dus
            studierichting[`d_${veld}`] = !!(val?.length && val.toUpperCase() === 'X'); //boolean
          } else {
            //een van de gewone props, we checken niet zonder spaties
            studierichting[veld] = val;
          }
        }
        //niveau altijd lowercase
        studierichting.niveau = <typeof studierichting.niveau>studierichting.niveau.toLowerCase();
        //we voegen een opleidingsniveau toe aan de studierichting
        if (studierichting.opleidingsniveau?.length) {
          studierichting.studierichting += `|(niveau: ${studierichting.opleidingsniveau})`; //die | geeft een break
        }
        nieuweLijst.push(studierichting);
      });
      if (errorstop) {
        return;
      }
      const niveaus = uniqBy(nieuweLijst, 'niveau').map(studie => studie.niveau.toLowerCase());
      if (niveaus.length > 1) {
        this.api.toast(`Het bestand bevat ${niveaus.length} niveaus. Elk importbestand mag maar één niveau bevatten`, "error");
        return;
      }
      //klaar, we gaan het melden
      if (!await this.api.confirm("Nieuwe excel importeren", `Het nieuwe bestand bevat informatie over ${nieuweLijst.length} studierichtingen op niveau ${niveaus[0]}. Deze gegevens vervangen alle huidige gegevens voor dat niveau. Weet je zeker dat je wilt doorgaan?`)) {
        return;
      }
      //we mogen
      this.reset();
      //we doen het buitenom: saven en laden
      const res = await this.api.action('importXLS', {
        niveau: niveaus[0],
        data: nieuweLijst.map(studie => studie.savedata())
      });
      if (!res.success) {
        this.api.toast("Het importeren is mislukt. De oude data staat nog in het systeem. Foutmelding: " + (res.melding || "Onbekende fout"), "warning");
      } else {
        this.studies = []; //even legen
        await this.haalData();
        this.filter.niveau = niveaus[0]; //even alleen de nieuwe dingen laten doen
        this.doeFilter();
        this.api.toast(`Het importeren is gelukt, je kijkt nu naar de nieuwe data van ${this.filter.niveau}`);
      }
    } catch (e) {
      this.logwarn(e);
      this.api.toast("Het importeren is mislukt", "warning");
    }

  }

  /**
   * Even wat dingen goedzetten
   * @protected
   */
  protected reset(): void {
    this.selectedRijNr = null;
    this.filter = {};
    this.doeFilter();
  }

  /**
   * Run het filter na een wijziging of reset
   */
  doeFilter(): void {
    this.studiesGefilterd = this.studies.filter(studie => this.inFilter(studie));
    this.selectedRijNr = null;
  }

  /**
   * UX, geef de juiste veldwaarde terug voor een profielkeuze
   * @param studie
   * @param profielkeuze
   * @protected
   */
  protected magProfielkeuze(studie: Studierichting, profielkeuze: string): string {
    let pk = studie.Profielkeuze?.find(pk => pk.profielkeuze === profielkeuze);
    if (pk) {
      return pk.opmerking?.length ? pk.opmerking : 'X';
    }
    return '';
  }

  /**
   * Return het studierichtingveld met op de plek van verticale strepen <br>
   * @param studie
   */
  breakStudierichting(studie: Studierichting) {
    return studie.studierichting.replace(/\s*\|\s*/g, '<br>');
  }
}
